/**
 * Checkbox component with Dashboard styling
 * Custom checkbox component that uses Dashboard theme colors instead of Chakra UI defaults
 * Integrates with React Hook Form
 *
 * @example
 * ```tsx
 * <Checkbox {...register('terms')}>
 *   I agree to the terms
 * </Checkbox>
 * ```
 */

"use client";

import {
  Checkbox as ChakraCheckbox,
  type CheckboxProps as ChakraCheckboxProps,
} from "@chakra-ui/react";
import { forwardRef } from "react";
import { useThemeTokens } from "@/lib/theme/tokens";
import { Text } from "@/components/ui";

export interface CheckboxProps extends ChakraCheckboxProps {}

export const Checkbox = forwardRef<HTMLInputElement, CheckboxProps>(
  ({ children, ...props }, ref) => {
    const theme = useThemeTokens();

    return (
      <ChakraCheckbox
        ref={ref}
        size="md"
        sx={{
          // Override all Chakra default colors with Dashboard theme
          "& .chakra-checkbox__control": {
            borderColor: `${theme.borderGlass.default} !important`,
            bg: "transparent !important",
            transition: "all 0.18s ease-out",
            "&[data-checked]": {
              bg: `${theme.accent.primary} !important`,
              borderColor: `${theme.accent.primary} !important`,
              color: "white !important",
              "&:hover": {
                bg: `${theme.accent.primary} !important`,
                borderColor: `${theme.accent.primary} !important`,
              },
            },
            "&[data-indeterminate]": {
              bg: `${theme.accent.primary} !important`,
              borderColor: `${theme.accent.primary} !important`,
              color: "white !important",
            },
            // Hover state for unchecked
            "&:hover:not([data-checked]):not([data-indeterminate]):not([data-disabled])": {
              borderColor: `${theme.accent.primaryBorder} !important`,
            },
          },
          // Checkmark icon SVG (uses currentColor, so we need to set color on control)
          "& .chakra-checkbox__control[data-checked] svg": {
            color: "white !important",
            stroke: "white !important",
          },
          "& .chakra-checkbox__control[data-indeterminate] svg": {
            color: "white !important",
            stroke: "white !important",
          },
          // Any child elements should also be white when checked
          "& .chakra-checkbox__control[data-checked] > *": {
            color: "white !important",
          },
          "& .chakra-checkbox__control[data-indeterminate] > *": {
            color: "white !important",
          },
          // Focus state
          "&:focus-visible .chakra-checkbox__control": {
            boxShadow: `${theme.focusRing} !important`,
          },
          "&:focus .chakra-checkbox__control": {
            boxShadow: `${theme.focusRing} !important`,
          },
          // Invalid state
          "&[data-invalid] .chakra-checkbox__control": {
            borderColor: `${theme.accent.primary} !important`,
          },
          // Disabled state
          "&[data-disabled] .chakra-checkbox__control": {
            opacity: "0.5 !important",
            cursor: "not-allowed !important",
          },
        }}
        {...props}
      >
        {children && (
          <Text 
            as="span" 
            display="inline-flex" 
            variant="primary" 
            fontSize="sm" 
            ml={2} 
            fontWeight="500"
            color={theme.text.primary}
          >
            {children}
          </Text>
        )}
      </ChakraCheckbox>
    );
  },
);

Checkbox.displayName = "Checkbox";
